'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { slaListMachine } from './machines/slaListMachine'
import type { CrmSlaPolicy, SlaPriority } from './types'

export type SlaListMachineApi = ReturnType<typeof useMachine<typeof slaListMachine>>

const SlaListMachineContext = createContext<SlaListMachineApi | null>(null)

export function SlaListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(slaListMachine)
  return <SlaListMachineContext.Provider value={api}>{children}</SlaListMachineContext.Provider>
}

export function useSlaListMachine() {
  const ctx = useContext(SlaListMachineContext)
  if (!ctx) {
    throw new Error('useSlaListMachine must be used within SlaListMachineProvider')
  }
  return ctx
}

export function useSlaListMachineOptional() {
  return useContext(SlaListMachineContext)
}

export function useSlaActions() {
  const machineApi = useSlaListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setPolicies: (policies: CrmSlaPolicy[]) => send({ type: 'SET_POLICIES', policies }),
    addPolicy: (policy: CrmSlaPolicy) => send({ type: 'ADD_POLICY', policy }),
    updatePolicy: (id: string, updates: Partial<CrmSlaPolicy>) =>
      send({ type: 'UPDATE_POLICY', id, updates }),
    deletePolicy: (id: string) => send({ type: 'DELETE_POLICY', id }),
    setSelectedPolicyId: (id: string | null) => send({ type: 'SET_SELECTED_POLICY_ID', id }),
  }), [send])
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useSlaListStoreCompat() {
  const machineApi = useSlaListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const policies = state.context.policies
    const selectedPolicyId = state.context.selectedPolicyId

    const getPolicyById = (id: string) => policies.find((policy) => policy.id === id)
    const getPolicyByTag = (parentTagId: string, childTagId?: string) =>
      policies.find(
        (policy) =>
          policy.parentTagId === parentTagId &&
          (!childTagId || policy.childTagId === childTagId) &&
          policy.isActive
      )
    const getPolicyByPriority = (priority: SlaPriority) =>
      policies.find((policy) => policy.priority === priority && policy.isActive)
    const getActivePolicies = () => policies.filter((policy) => policy.isActive)

    return {
      policies,
      selectedPolicyId,
      setPolicies: (p: CrmSlaPolicy[]) => send({ type: 'SET_POLICIES', policies: p }),
      addPolicy: (policy: CrmSlaPolicy) => send({ type: 'ADD_POLICY', policy }),
      updatePolicy: (id: string, updates: Partial<CrmSlaPolicy>) =>
        send({ type: 'UPDATE_POLICY', id, updates }),
      deletePolicy: (id: string) => send({ type: 'DELETE_POLICY', id }),
      setSelectedPolicyId: (id: string | null) => send({ type: 'SET_SELECTED_POLICY_ID', id }),
      getPolicyById,
      getPolicyByTag,
      getPolicyByPriority,
      getActivePolicies,
    }
  }, [machineApi])
}
